import { MoreHorizontalIcon } from "lucide-react";

import type { CloudEnvironmentCatalogRow } from "../../cmmd/cmmdForgeRuntimeListResource";
import type { CmmdForgeRuntimeLifecycleAction } from "../../environments/primary/cmmdForgeRuntimeApi";
import { Button } from "../ui/button";
import { Menu, MenuItem, MenuPopup, MenuSeparator, MenuTrigger } from "../ui/menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import {
  cloudRuntimeActionIcon,
  type CloudRuntimeActionDescriptor,
  DELETE_ENVIRONMENT_LABEL,
  deleteEnvironmentIcon,
} from "./cloudRuntimeRowActions";

/**
 * The row's secondary and irreversible actions, behind one labelled trigger.
 *
 * Every action on this row used to be an icon with a hover tooltip and nothing
 * else — six of them on the widest row. Two problems, and the second is the
 * one that mattered: a wall of wrench/pause/rotate/download/trash glyphs is
 * unreadable at a glance, and a hover tooltip does not exist on a touch
 * device, so on a phone the labels were unreachable by any means. The row
 * therefore asked the user to already know what each glyph did.
 *
 * A menu answers both. Each item carries its icon AND its full
 * consequence-bearing label (for example, "Pause environment"), so the text
 * that was hidden behind hover is now simply visible, on every pointer type.
 * `docs/design/row-action-buttons.md` scopes the icon-only rule to clusters of
 * 2+ visible per-row actions; promoting one action out and collapsing the rest
 * leaves this row outside it by design, not by evasion.
 */
export const ROW_MENU_LABEL = "Advanced";

export function ConnectionRowActionMenu({
  environment,
  actions,
  isBusy,
  busyLabel,
  onUpdate,
  onDeleteEnvironment,
  onRecoverEnvironment,
  onRunLifecycleAction,
}: {
  readonly environment: CloudEnvironmentCatalogRow;
  /** The lifecycle actions NOT promoted to the row's visible primary button. */
  readonly actions: readonly CloudRuntimeActionDescriptor[];
  readonly isBusy: boolean;
  readonly busyLabel: string | null;
  readonly onUpdate: (environment: CloudEnvironmentCatalogRow) => void;
  /** Destroys the Sprite and removes the Environment. */
  readonly onDeleteEnvironment: (environment: CloudEnvironmentCatalogRow) => void;
  readonly onRecoverEnvironment: () => void;
  readonly onRunLifecycleAction: (
    environment: CloudEnvironmentCatalogRow,
    action: CmmdForgeRuntimeLifecycleAction,
  ) => void;
}) {
  const menuLabel = isBusy && busyLabel !== null ? busyLabel : ROW_MENU_LABEL;
  const runAction = (action: CloudRuntimeActionDescriptor) => {
    if (action.kind === "update") {
      onUpdate(environment);
      return;
    }
    if (action.kind === "recover") {
      onRecoverEnvironment();
      return;
    }
    onRunLifecycleAction(environment, action.kind);
  };

  return (
    <Menu>
      <Tooltip>
        <TooltipTrigger
          render={
            <MenuTrigger
              render={
                <Button
                  size="xs"
                  variant="ghost"
                  aria-label={menuLabel}
                  className="max-md:min-h-touch max-md:min-w-touch max-md:rounded-xl"
                  disabled={isBusy}
                />
              }
            >
              {menuLabel}
              <MoreHorizontalIcon aria-hidden="true" className="size-4" />
            </MenuTrigger>
          }
        />
        <TooltipPopup side="top">{menuLabel}</TooltipPopup>
      </Tooltip>
      <MenuPopup align="end">
        {actions.map((action) => (
          <MenuItem key={action.kind} onClick={() => runAction(action)}>
            {cloudRuntimeActionIcon(action.kind)}
            {action.label}
          </MenuItem>
        ))}
        {actions.length > 0 ? <MenuSeparator /> : null}
        {/* Offered whether or not a Sprite was ever provisioned — the row is
            the environment, and an unprovisioned one still has to be removable
            without first creating the machine you are trying to delete. */}
        <MenuItem variant="destructive" onClick={() => onDeleteEnvironment(environment)}>
          {deleteEnvironmentIcon()}
          {DELETE_ENVIRONMENT_LABEL}
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}
